/**
 * The launcher must never cover the shop's own controls at the bottom of the screen: the sticky «Aggiungi al carrello»
 * bar of Shopify themes (Horizon: full width on phones, button on the right), cookie bars, app banners. When such a bar
 * sits under the launcher, the launcher (and its teaser) move just above it through `--aca-bottom`; when it goes away
 * they come back down. Generic: any fixed/sticky element anchored to the bottom edge, no theme-specific selectors.
 */

const GAP = 12;
/** Taller than this share of the viewport it is a drawer or an overlay, not a bar: the launcher stays put. */
const MAX_BAR_SHARE = 0.4;
/** One more look once the page has settled (cookie banner shown after the consent script loads). */
const SETTLE_MS = 1500;

/** Lowest edge offset the launcher needs (px from the viewport bottom), or 0 when nothing of the site is under it. */
export function bottomLift(win: Window, host: Element, left: boolean): number {
  const doc = win.document;
  if (typeof doc.elementsFromPoint !== 'function') return 0;
  const vw = win.innerWidth;
  const vh = win.innerHeight;
  if (!vw || !vh) return 0;
  const x = left ? 44 : vw - 44;
  let lift = 0;
  for (const y of [vh - 6, vh - 44]) {
    for (const el of doc.elementsFromPoint(x, y)) {
      if (el === host || el === doc.documentElement || el === doc.body) continue;
      const bar = fixedAncestor(win, el);
      if (!bar || bar === host) continue;
      const r = bar.getBoundingClientRect();
      if (r.height <= 0 || r.height > vh * MAX_BAR_SHARE || r.bottom < vh - 2) continue;
      lift = Math.max(lift, Math.ceil(vh - r.top));
    }
  }
  return lift;
}

function fixedAncestor(win: Window, el: Element): Element | null {
  for (let n: Element | null = el; n && n !== win.document.body; n = n.parentElement) {
    const pos = win.getComputedStyle(n).position;
    if (pos === 'fixed' || pos === 'sticky') return n;
  }
  return null;
}

/** Keeps `--aca-bottom` on `root` in step with the bars of the page; returns the teardown. */
export function watchBottomBars(win: Window, root: HTMLElement, host: Element, isLeft: () => boolean): () => void {
  let pending = false;
  let frame = 0;
  let last = -1;
  const apply = () => {
    pending = false;
    const lift = bottomLift(win, host, isLeft());
    if (lift === last) return;
    last = lift;
    if (lift > 0) root.style.setProperty('--aca-bottom', `${lift + GAP}px`);
    else root.style.removeProperty('--aca-bottom');
  };
  const schedule = () => {
    if (pending) return;
    pending = true;
    frame = win.requestAnimationFrame(apply);
  };
  // Horizon shows its sticky add-to-cart bar on scroll; resize covers rotation and the virtual keyboard
  win.addEventListener('scroll', schedule, { passive: true, capture: true });
  win.addEventListener('resize', schedule, { passive: true });
  const settle = win.setTimeout(schedule, SETTLE_MS);
  schedule();
  return () => {
    win.removeEventListener('scroll', schedule, { capture: true });
    win.removeEventListener('resize', schedule);
    win.clearTimeout(settle);
    if (pending) win.cancelAnimationFrame(frame);
  };
}
